import React, { useEffect, useState } from 'react';
import DashboardLayout from '@layouts/dashboard/dashboard-layout';
import { useNavigate } from 'react-router-dom';
import { QueryRequestHelper } from '@common/query-request-helper';
import { deleteSpecificExpenseEntry } from '@services/expense-entry.service';
import { getValue } from '@utils/lodash';
import { toast } from 'react-toastify';
import DeleteModal from '@common/popup/DeleteModal';
import PaginationPage from '@components/pagination/pagination';
import NavigationBar from '@components/NotificationBar';
import SearchHeader from '@components/common/SearchHeader';
import Loader from '@components/common/Loader';
import Notfound from '@components/Not-Found';
import './trips.scss';
import TripsTable from './table';
import { formatText } from '@common/text-helpers';
import { getAllTrips } from '@services/trips.service';
import NoAccessPage from '@components/common/NoAccess';
import TripsNotfound from '@components/Not-Found/trips-not-found';
import SkeletonTable from '@components/common/Skeleton/skeleton-table';
import { getMyProfile } from '@services/users.service';
import Select from 'react-select';
import { usersSearchableDropdown } from '@components/common/CustomSearchableDropdown/SearchAPIs';

function MyViewsTrips(props: any) {
  let navigate = useNavigate();
  const urlSearchParams = new URLSearchParams(window.location.search);
  const urlParams = Object.fromEntries(urlSearchParams.entries());
  let mainQueryRequest = QueryRequestHelper(urlParams);

  /* -------------------------------------------------------------------------- */
  /*                               UseState Section                             */
  /* -------------------------------------------------------------------------- */

  const [isLoading, setIsLoading] = React.useState(true);
  const [list, setList] = React.useState<[]>([]);

  // Created For dropdown states
  const [userProfile, setUserProfile] = useState<any>({});
  const [createdFor, setCreatedFor] = useState<any>(null);
  const [createdForOptions, setCreatedForOptions] = useState([
    { value: 'self', label: 'Self' },
  ]);

  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      minHeight: '32px',
      height: '32px',
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      height: '32px',
      padding: '0 6px',
    }),
    input: (provided: any) => ({
      ...provided,
      margin: '0px',
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      height: '32px',
    }),
  };

  /* -------------------------------------------------------------------------- */
  /*                            Created For Functions                           */
  /* -------------------------------------------------------------------------- */

  const getUserProfileData = async () => {
    try {
      let resp = await getMyProfile('');
      if (resp) {
        const profileData = getValue(resp, `data`, {});
        setUserProfile(profileData);
        setCreatedFor({ value: 'self', label: 'Self' });
        await getDelegatedUsersWithUserId(getValue(profileData, `id`, ''));
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const getDelegatedUsersWithUserId = async (userId: string | number) => {
    if (!userId) return;

    try {
      const userIdNumber =
        typeof userId === 'string' ? parseInt(userId, 10) : userId;

      const delegatedUsers = await usersSearchableDropdown(
        '',
        '',
        userIdNumber,
      );

      if (delegatedUsers && delegatedUsers.length > 0) {
        const delegatedOptions = delegatedUsers.map((user: any) => ({
          value: String(getValue(user, `id`, '')),
          label: getValue(user, `workflow_name`, ''),
        }));

        setCreatedForOptions([
          { value: 'self', label: 'Self' },
          ...delegatedOptions,
        ]);
      } else {
        setCreatedForOptions([{ value: 'self', label: 'Self' }]);
      }
    } catch (error) {
      console.error('Error fetching delegated users:', error);
      setCreatedForOptions([{ value: 'self', label: 'Self' }]);
    }
  };

  const handleCreatedForChange = (selectedOption: any) => {
    setCreatedFor(selectedOption);
  };

  /* -------------------------------------------------------------------------- */
  /*                               UseEffect Section                            */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    getUserProfileData();
  }, []);

  useEffect(() => {
    if (Object.keys(urlParams).length === 0 && props.permissionAPITriggered) {
      getData();
    }
  }, [props.permissionAPITriggered]);

  useEffect(() => {
    if (Object.keys(urlParams).length > 0 && props.permissionAPITriggered) {
      if (urlParams.page_no) {
        setPage_no(parseInt(urlParams.page_no));
      }
      if (urlParams.search_text) {
        setSearchInput(urlParams.search_text);
        setShowSearchTextbox(true);
      }
      if (urlParams.limit) {
        setLimit(parseInt(urlParams.limit));
      }
      getData();
    }
  }, [window.location.href, props.permissionAPITriggered]);

  /* -------------------------------------------------------------------------- */
  /*                               API Section                                  */
  /* -------------------------------------------------------------------------- */

  const getData = async () => {
    let payload: any = {
      page_no: urlParams.page_no ? parseInt(urlParams.page_no) : 1,
      page_size: urlParams.limit ? parseInt(urlParams.limit) : limit,
      search_text: urlParams.search_text,
      approval_status: urlParams.approval_status,
      sort_by: urlParams.sort_by,
      travel_mode: 'flight',
      filter_type: 'list',
    };

    // Add delegate filter if not "self"
    if (createdFor && getValue(createdFor, `value`, '') !== 'self') {
      payload.submitted_for = Number(getValue(createdFor, `value`, ''));
    }

    let queryRequest = QueryRequestHelper(payload);
    try {
      setIsLoading(true);
      let resp = await getAllTrips(queryRequest);
      if (resp) {
        setList(getValue(resp, `data.trip`, []));
        setTotalCount(getValue(resp, `data.pagination.total`, 0));
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  // Re-fetch data when createdFor changes
  useEffect(() => {
    if (createdFor && props.permissionAPITriggered) {
      getData();
    }
  }, [createdFor]);

  /* -------------------------------------------------------------------------- */
  /*                               Search section                               */
  /* -------------------------------------------------------------------------- */
  const [showSearchTextbox, setShowSearchTextbox] = useState(false);
  const [search_text, setSearchInput] = useState<any>('');

  const toggleSearch = () => {
    setShowSearchTextbox((prevState) => !prevState);
  };
  const toggleSearchClose = () => {
    setSearchInput('');
    toggleSearch();
    if (urlParams.search_text) {
      delete urlParams.search_text;
      let payload: any = { ...urlParams };
      let queryRequest = QueryRequestHelper(payload);

      navigate(`${window.location.pathname}?${queryRequest}`);
      getData();
    }
  };

  const handleChangeSearch = async (e: any) => {
    setSearchInput(e.target.value);

    let payload: any = {
      ...urlParams,
      page_no: 1,
      search_text: e.target.value,
    };
    let queryRequest = QueryRequestHelper(payload);
    navigate(`${window.location.pathname}?${queryRequest}`);
    if (!e.target.value) {
      getData();
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                               Onchange section                             */
  /* -------------------------------------------------------------------------- */

  const [selectedIDs, setSelectedIDs] = useState<any>([]);

  const handleCheckUsers = (id: string) => {
    let userIDs =
      getValue(selectedIDs, `length`, 0) > 0
        ? selectedIDs.map((item: string) => item)
        : [];
    if (userIDs.includes(id)) {
      let filterIDs = selectedIDs.filter((item: string) => item !== id);
      setSelectedIDs(filterIDs);
    } else {
      selectedIDs.push(id);
      setSelectedIDs([...selectedIDs]);
    }
  };
  const handleCheckAll = (status: string) => {
    if (status === 'All') {
      setSelectedIDs([]);
    } else {
      let userIDs =
        getValue(list, `length`, 0) > 0
          ? list.map((item: object) => getValue(item, `id`, ''))
          : [];
      setSelectedIDs(userIDs);
    }
  };

  const handleNavigateURL = (key: string, value: string) => {
    if (value) {
      let payload = {
        ...urlParams,
        [key]: value,
      };
      let queryRequest = QueryRequestHelper(payload);
      navigate(`${window.location.pathname}?${queryRequest}`);
    } else {
      delete urlParams[key];
      let payload = {
        ...urlParams,
      };
      let queryRequest = QueryRequestHelper(payload);
      navigate(`${window.location.pathname}?${queryRequest}`);
      getData();
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                                 Delete section                             */
  /* -------------------------------------------------------------------------- */

  const [deleteValue, setDeleteValue] = useState('');
  const [deleteId, setDeleteId] = useState('');
  const handleDeleteFun = (id: string, value: string) => {
    setDeleteValue(value);
    setDeleteId(id);
    handleDelete();
  };
  const [isDelete, setIsDelete] = useState(false);
  const handleDelete = () => {
    setIsDelete(!isDelete);
  };

  const handleDeleteFunction = async () => {
    try {
      let resp = await deleteSpecificExpenseEntry(deleteId);
      if (resp) {
        toast.success(getValue(resp, `message`, ''));
        handleDelete();
        getData();
      }
    } catch (error) {}
  };

  /* -------------------------------------------------------------------------- */
  /*                               Pagination section                           */
  /* -------------------------------------------------------------------------- */

  const [totalCount, setTotalCount] = useState(0);
  const [limit, setLimit] = useState(10);
  const [page_no, setPage_no] = useState(1);

  const handleChangePagination = (page_no: string) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const urlParams = Object.fromEntries(urlSearchParams.entries());
    let payload = {
      ...urlParams,
      page_no: page_no,
    };
    let queryRequest = QueryRequestHelper(payload);
    navigate(`${window.location.pathname}?${queryRequest}`);
  };
  const handleChangeLimit = (limit: string) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const urlParams = Object.fromEntries(urlSearchParams.entries());
    let payload: any = {
      ...urlParams,
      limit: limit,
      page_no: '1',
    };
    let queryRequest = QueryRequestHelper(payload);
    navigate(`${window.location.pathname}?${queryRequest}`);
  };
  const handleChangeActiveStatus = async (value: string) => {
    // try {
    //   let resp = await adminBulkUpdateStatus({
    //     ids: selectedIDs,
    //     is_active: value === "active" ? true : false,
    //   });
    //   if (resp) {
    //     toast.success(getValue(resp, `message`, ""));
    //     setSelectedIDs([]);
    //     getData();
    //   } else {
    //     getData();
    //   }
    // } catch (error) {}
  };
  const [check, setCheck] = useState(false);
  const handleRemoveNavItems = () => {
    setCheck(!check);
    setSelectedIDs([]);
  };
  const handleNavigateUserEntity = () => {
    if (getValue(selectedIDs, `[${0}]`, '')) {
      navigate(
        `/admin/user/entity-mapping/${getValue(selectedIDs, `[${0}]`, '')}`,
      );
    } else {
      toast.error('Please select User');
    }
  };
  const handleNavigate = (id: string) => {
    navigate(`/client/trips/detail/${id}?${mainQueryRequest}`);
  };

  const [filterOptions] = useState([
    { value: 'unsubmitted', label: formatText('unsubmitted') },
    { value: 'awaiting_approval', label: formatText('awaiting_approval') },
    { value: 'approved', label: formatText('approved') },
    { value: 'rejected', label: formatText('rejected') },
    { value: 'reimbursed', label: formatText('reimbursed') },
    {
      value: 'cancel_before_booking',
      label: formatText('cancel_before_booking'),
    },
    {
      value: 'cancel_after_booking',
      label: formatText('cancel_after_booking'),
    },
    // { value: "expired", label: formatText("expired") },
    // { value: "archived", label: formatText("archived") },
  ]);

  return (
    <DashboardLayout
      permissions={getValue(props, `tabPermissionList`, [])}
      subTabPermissionList={getValue(props, `subTabPermissionList`, [])}
    >
      {!isLoading &&
      !getValue(props, `subTabPermissionList`, []).includes('trips') ? (
        <NoAccessPage />
      ) : (
        <>
          <div className="dashboard-wrapper__header">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <h4 className="dashbaord-wrapper__header-title">Trips</h4>

              {/* View As Dropdown - aligned to the right */}
              {createdForOptions.length > 1 && (
                <div className="d-flex align-items-center gap-2">
                  <h6 className="small_text__14 color_primary mt-1 me-2">
                    View as:
                  </h6>
                  <div style={{ minWidth: '150px' }}>
                    <Select
                      styles={customStyles}
                      options={createdForOptions}
                      onChange={handleCreatedForChange}
                      value={createdFor}
                      isClearable={false}
                      placeholder="Select user"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {getValue(selectedIDs, `length`, 0) > 0 && (
            <NavigationBar
              selectedIDs={selectedIDs}
              handleChangeActiveStatus={handleChangeActiveStatus}
              handleRemoveNavItems={handleRemoveNavItems}
              multi={
                getValue(props, `permissions`, []).includes('update')
                  ? true
                  : false
              }
              permissions={getValue(props, `permissions`, [])}
              entityMapping={
                getValue(props, `products`, []).includes('SELFCOMP')
                  ? true
                  : false
              }
              handleNavigateUserEntity={handleNavigateUserEntity}
            />
          )}
          <div className="admin-wrapper position-relative">
            <div className="admin-wrapper position-relative">
              <SearchHeader
                route={`/client/trips/add${
                  createdFor && getValue(createdFor, `value`, '') !== 'self'
                    ? `?created_for=${getValue(createdFor, `value`, '')}`
                    : ''
                }`}
                searchInput={search_text}
                showSearchTextbox={showSearchTextbox}
                toggleSearch={toggleSearch}
                toggleSearchClose={toggleSearchClose}
                handleChangeSearch={handleChangeSearch}
                reportFilter
                filterOptions={filterOptions}
                handleNavigateURL={handleNavigateURL}
                add={
                  getValue(props, `permissions`, []).includes('create')
                    ? 'true'
                    : 'false'
                }
                filter_key="approval_status"
              />
            </div>
          </div>
          <div
            className={
              getValue(selectedIDs, `length`, 0) > 0
                ? 'datatable_main_container_notifiction'
                : 'datatable_main_container'
            }
            style={{ overflowY: isLoading ? 'hidden' : 'auto' }}
          >
            {isLoading || !props.permissionAPITriggered ? (
              // <Loader />
              <SkeletonTable />
            ) : getValue(list, `length`, 0) > 0 ? (
              <TripsTable
                list={list}
                selectedIDs={selectedIDs}
                handleCheckUsers={handleCheckUsers}
                handleCheckAll={handleCheckAll}
                isLoading={isLoading}
                handleDeleteFun={handleDeleteFun}
                handleNavigate={handleNavigate}
              />
            ) : (
              <>
                <TripsNotfound />
              </>
            )}
          </div>
          <div className="fixed_footer_wrapper d-flex justify-content-between">
            {list.length > 0 && (
              <img
                src="/images/building-illustration.svg"
                className="img-fluid"
              />
            )}
            {totalCount > 10 ? (
              <PaginationPage
                totalCount={totalCount}
                limit={limit}
                pageNumber={page_no}
                handleChangePagination={handleChangePagination}
                handleChangeLimit={handleChangeLimit}
              />
            ) : (
              <p className="show-rows-count mt-3 d-flex">
                Total Count:&nbsp;&nbsp;{' '}
                <p style={{ color: 'rgb(13, 110, 253)' }}>{totalCount}</p>
              </p>
            )}
          </div>
          <DeleteModal
            isOpen={isDelete}
            handleModal={handleDelete}
            handleSubmit={handleDeleteFunction}
            deleteValue={deleteValue}
          />
        </>
      )}
    </DashboardLayout>
  );
}

export default MyViewsTrips;
